import React from 'react';

type AlertSeverity = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
    severity?: AlertSeverity;
    title?: string;
    children?: React.ReactNode;
    onClose?: () => void;
    className?: string;
}

const severityStyles: Record<AlertSeverity, { container: string; icon: string; title: string }> = {
    success: {
        container: 'bg-green-50 text-green-800 border border-green-200',
        icon: 'text-green-500',
        title: 'text-green-900',
    },
    error: {
        container: 'bg-red-50 text-red-800 border border-red-200',
        icon: 'text-red-500',
        title: 'text-red-900',
    },
    warning: {
        container: 'bg-yellow-50 text-yellow-800 border border-yellow-200',
        icon: 'text-yellow-500',
        title: 'text-yellow-900',
    },
    info: {
        container: 'bg-blue-50 text-blue-800 border border-blue-200',
        icon: 'text-blue-500',
        title: 'text-blue-900',
    },
};

const icons: Record<AlertSeverity, React.JSX.Element> = {
    success: (
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293A1 1 0 106.293 10.707l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
            />
        </svg>
    ),
    error: (
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
                fillRule="evenodd"
                d="M18 10A8 8 0 11.001 10 8 8 0 0118 10zM9 5a1 1 0 112 0v5a1 1 0 11-2 0V5zm1 10a1.25 1.25 0 100-2.5A1.25 1.25 0 0010 15z"
                clipRule="evenodd"
            />
        </svg>
    ),
    warning: (
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.518 11.585c.75 1.335-.212 3.016-1.742 3.016H3.48c-1.53 0-2.492-1.681-1.742-3.016L8.257 3.1zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-2a1 1 0 01-1-1V7a1 1 0 112 0v3a1 1 0 01-1 1z" />
        </svg>
    ),
    info: (
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
                fillRule="evenodd"
                d="M18 10A8 8 0 11.001 10 8 8 0 0118 10zM9 9.5A1.5 1.5 0 1010.5 8 1.5 1.5 0 009 9.5zM11 14a1 1 0 10-2 0v1a1 1 0 102 0v-1z"
                clipRule="evenodd"
            />
        </svg>
    ),
};

const Alert: React.FC<AlertProps> = ({ severity = 'info', title, children, onClose, className }) => {
    const styles = severityStyles[severity];

    return (
        <div
            role="alert"
            className={`w-full rounded-md px-4 py-3 flex gap-3 items-start ${styles.container} ${className ?? ''}`}
        >
      <span className={`${styles.icon} mt-0.5`} aria-hidden="true">
        {icons[severity]}
      </span>
            <div className="flex-1">
                {title ? <p className={`font-semibold ${styles.title}`}>{title}</p> : null}
                {children ? <div className="text-sm">{children}</div> : null}
            </div>
            {onClose ? (
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Cerrar"
                    className="shrink-0 rounded p-1 hover:bg-black/5 transition"
                >
                    <svg className="h-4 w-4 text-current" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414
              1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10
              11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10
              4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
            ) : null}
        </div>
    );
};

export default Alert;